import { z } from 'zod';

/**
 * Sales-tax contracts shared by every provider (the in-repo rate table and
 * Stripe Tax). Money is integer cents everywhere here, the same unit the
 * PaymentIntent is built in, so no provider is allowed to introduce its own
 * rounding on the way to the charge.
 */

export const TAX_COUNTRIES = ['US', 'CA'] as const;
export const TaxCountrySchema = z.enum(TAX_COUNTRIES);
export type TaxCountry = z.infer<typeof TaxCountrySchema>;

/**
 * What a listing IS for tax purposes. A marketplace category tells us this
 * (see product-tax-classes.ts); the rate tables key their exemptions on it.
 */
export const PRODUCT_TAX_CLASSES = [
  'general', // tangible personal property, the default
  'clothing', // adult clothing and footwear
  'childrens_clothing', // clothing sized for children (several jurisdictions exempt only this)
  'books', // physical printed books
  'groceries', // food for home consumption (not sold on the marketplace today; kept for the tables)
  'pet_food',
  'live_animals', // pets and livestock
  'motor_vehicle', // titled vehicles: taxed at registration by the DMV, not at the register
  'digital_goods', // downloads with no physical delivery
  'service', // nothing tangible changes hands
] as const;
export const ProductTaxClassSchema = z.enum(PRODUCT_TAX_CLASSES);
export type ProductTaxClass = z.infer<typeof ProductTaxClassSchema>;

export const TaxAddressSchema = z.object({
  country: TaxCountrySchema,
  /** Two-letter state or province code, upper case (CA, NY, ON, BC). */
  region: z.string().trim().toUpperCase().min(2).max(2),
  postalCode: z.string().trim().min(1).max(20),
  city: z.string().trim().max(100).optional(),
  line1: z.string().trim().max(255).optional(),
});
export type TaxAddress = z.infer<typeof TaxAddressSchema>;

export const TaxLineItemSchema = z.object({
  /** Caller-chosen id (listing id) echoed back so the caller can match lines. */
  reference: z.string().min(1),
  amountCents: z.number().int().min(0),
  quantity: z.number().int().min(1).default(1),
  productTaxClass: ProductTaxClassSchema.default('general'),
});
export type TaxLineItem = z.infer<typeof TaxLineItemSchema>;

export const TaxQuoteRequestSchema = z.object({
  currency: z.enum(['usd', 'cad']).default('usd'),
  shipTo: TaxAddressSchema,
  /**
   * Where the goods leave from. Origin-sourced states (TX, PA, ...) and the
   * Canadian import rule both need it. When unknown: in the US the
   * destination rules apply, which is right for a remote seller; for a
   * Canadian buyer an unknown origin is treated as outside Canada, because
   * Sellwild's sellers are US-based unless the origin says otherwise.
   */
  shipFrom: TaxAddressSchema.optional(),
  lineItems: z.array(TaxLineItemSchema).min(1),
  shippingCents: z.number().int().min(0).default(0),
  /** The buyer holds a resale/exemption certificate on file. Never true today. */
  buyerExempt: z.boolean().default(false),
});
export type TaxQuoteRequest = z.infer<typeof TaxQuoteRequestSchema>;
export type TaxQuoteRequestInput = z.input<typeof TaxQuoteRequestSchema>;

/**
 * Why a line came back with the tax it did. `not_registered` is the one that
 * matters operationally: collecting tax where you are not registered to remit
 * it is the one thing worse than not collecting at all.
 */
export const TAXABILITY_REASONS = [
  'standard_rated',
  'reduced_rated',
  'product_exempt',
  'shipping_exempt',
  'not_registered',
  'no_sales_tax',
  'buyer_exempt',
  'collected_at_import',
  'taxed_at_registration',
  /** The provider could not answer and the sale was charged no tax rather than refused. */
  'unavailable',
] as const;
export const TaxabilityReasonSchema = z.enum(TAXABILITY_REASONS);
export type TaxabilityReason = z.infer<typeof TaxabilityReasonSchema>;

export const TaxJurisdictionLevelSchema = z.enum([
  'country',
  'state',
  'province',
  'county',
  'city',
  'district',
  'local_average',
]);

export const TaxBreakdownEntrySchema = z.object({
  jurisdiction: z.object({
    country: TaxCountrySchema,
    region: z.string().optional(),
    level: TaxJurisdictionLevelSchema,
    displayName: z.string(),
    /**
     * The jurisdiction's own code, where the rate came from a source that has
     * one: a FIPS county or place code, a Washington location code, Alabama's
     * SSUT programme code. A return is filed per jurisdiction code, so a
     * breakdown without these is a number nobody can remit; everything the
     * table averages instead leaves it unset.
     */
    code: z.string().max(16).optional(),
  }),
  /** Tax type label as the receipt should print it (Sales Tax, GST, HST, PST, QST). */
  taxType: z.string(),
  /** Percentage as a number, 6.25 means 6.25%. */
  ratePercent: z.number().min(0),
  taxableAmountCents: z.number().int().min(0),
  amountCents: z.number().int().min(0),
  reason: TaxabilityReasonSchema,
});
export type TaxBreakdownEntry = z.infer<typeof TaxBreakdownEntrySchema>;

export const TaxQuoteLineSchema = z.object({
  reference: z.string(),
  amountCents: z.number().int().min(0),
  taxCents: z.number().int().min(0),
  productTaxClass: ProductTaxClassSchema,
  reason: TaxabilityReasonSchema,
});
export type TaxQuoteLine = z.infer<typeof TaxQuoteLineSchema>;

export const TaxQuoteSchema = z.object({
  provider: z.enum(['table', 'stripe', 'none']),
  currency: z.enum(['usd', 'cad']),
  /** Sum of line taxes plus shipping tax. This is what gets added to the charge. */
  taxCents: z.number().int().min(0),
  /** Effective combined rate on the taxable base, for display only. */
  effectiveRatePercent: z.number().min(0),
  lines: z.array(TaxQuoteLineSchema),
  shipping: z.object({
    amountCents: z.number().int().min(0),
    taxCents: z.number().int().min(0),
    reason: TaxabilityReasonSchema,
  }),
  breakdown: z.array(TaxBreakdownEntrySchema),
  /**
   * Whether any rate in this quote is a stand-in for the buyer's own.
   *
   * False means every component came from a rate that applies to the buyer's
   * address exactly: a state with no local sales tax at all, a ZIP whose local
   * jurisdictions all charge the same, Alabama's flat marketplace rate, or a
   * Canadian province. True means at least one figure is the top of a range or
   * a statewide average, and the receipt says "approx." A table quote is not
   * automatically approximate and never was; the old code assumed it.
   */
  approximate: z.boolean().default(true),
  /**
   * Provider handle to persist on the PaymentIntent metadata. For Stripe it is
   * the tax calculation id, which is what turns into a Tax Transaction after
   * capture; for the table it is a content hash of the inputs.
   */
  calculationId: z.string(),
  /** ISO timestamp after which the provider will not honour this quote. */
  expiresAt: z.string().nullable(),
});
export type TaxQuote = z.infer<typeof TaxQuoteSchema>;

/** The seam both providers implement; checkout only ever talks to this. */
export interface SalesTaxProvider {
  readonly name: TaxQuote['provider'];
  quote(request: TaxQuoteRequestInput): Promise<TaxQuote>;
  /**
   * Called once the payment is captured, so the provider can record the sale.
   * Only the handle survives from checkout to capture (it travels on the
   * PaymentIntent metadata), so that is all this takes.
   */
  record(quote: Pick<TaxQuote, 'provider' | 'calculationId'>, reference: string): Promise<{ transactionId: string | null }>;
  /** Called on refund with the cents refunded, so the recorded sale is reversed. */
  reverse(
    transactionId: string,
    reference: string,
    refundedCents: number | 'full',
  ): Promise<{ reversalId: string | null }>;
}
